import {
  RANGE_PRESETS,
  resolveRange,
  validateRangeSelection,
  type RangePreset,
  type RangeSelection,
  type ResolvedRange,
  type Timestamp,
} from '@anytracker/core'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

import { useServices } from '../services/ServicesProvider.js'

/**
 * One answer to "what does last 30 days mean", shared by every screen (T-402).
 *
 * The timeline, both charts, the CSV export and the PDF report all ask it, and
 * they have to agree — an export that covers a different fortnight from the
 * chart it was generated beside is worse than no export. Resolution itself
 * lives in core; this holds the selection and the entry bounds `all time`
 * needs.
 */

const STORAGE_KEY = 'anytracker.range'

/**
 * All time, not a recent window.
 *
 * The timeline is "everything, newest first" and has been since T-305; opening
 * it on the last thirty days would silently hide history the user knows they
 * recorded. A chart of all time is equally reasonable — `defaultBucketSize`
 * picks a granularity that fits — and narrowing is one tap away and then sticks
 * across screens.
 */
export const DEFAULT_RANGE: RangeSelection = { kind: 'preset', preset: 'allTime' }

export interface RangeContextValue {
  readonly selection: RangeSelection
  readonly setSelection: (selection: RangeSelection) => void
  /** The selection as absolute instants in the device's current zone. */
  readonly resolved: ResolvedRange
  /**
   * Whether the entry bounds have been read yet.
   *
   * Until they have, "all time" resolves to today — which is a perfectly valid
   * range that contains almost nothing. A screen that queries before this is
   * true renders an empty chart and then replaces it, and anything it decided
   * from that first empty result (which track to show, say) is wrong and
   * sticky.
   */
  readonly ready: boolean
  /** Re-reads the first and last entry, after something is logged or deleted. */
  readonly refreshBounds: () => Promise<void>
}

const RangeContext = createContext<RangeContextValue | null>(null)

/**
 * Reads a selection out of session storage.
 *
 * Session rather than local: a range is a thing you are looking at now, not a
 * preference. Coming back tomorrow to a window that ended three weeks ago,
 * silently, is worse than starting from the default every time.
 */
function readStored(): RangeSelection {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (raw === null) {
      return DEFAULT_RANGE
    }
    const parsed: unknown = JSON.parse(raw)
    // Checked through the same validator the control uses, rather than cast on
    // the strength of a `kind` field. Storage is not a type system: a truncated
    // write leaves a selection that looks well-formed and resolves to nothing.
    if (typeof parsed === 'object' && parsed !== null && 'kind' in parsed) {
      if (
        parsed.kind === 'preset' &&
        'preset' in parsed &&
        RANGE_PRESETS.some((known) => known === parsed.preset)
      ) {
        return { kind: 'preset', preset: parsed.preset as RangePreset }
      }
      if (
        parsed.kind === 'custom' &&
        'fromDay' in parsed &&
        'toDay' in parsed &&
        typeof parsed.fromDay === 'string' &&
        typeof parsed.toDay === 'string'
      ) {
        const candidate: RangeSelection = {
          kind: 'custom',
          fromDay: parsed.fromDay,
          toDay: parsed.toDay,
        }
        return validateRangeSelection(candidate).ok ? candidate : DEFAULT_RANGE
      }
    }
  } catch {
    // A quota error, a disabled storage, or something else's key under ours.
    // None of them are worth failing a render over.
  }
  return DEFAULT_RANGE
}

export function RangeProvider({ children }: { children: ReactNode }) {
  const { repos, clock } = useServices()
  const [selection, setStored] = useState<RangeSelection>(readStored)
  const [bounds, setBounds] = useState<{ first?: Timestamp; last?: Timestamp }>({})
  const [ready, setReady] = useState(false)

  const refreshBounds = useCallback(async () => {
    const found = await repos.entries.bounds()
    setBounds({
      ...(found.first === undefined ? {} : { first: found.first.at }),
      ...(found.last === undefined ? {} : { last: found.last.at }),
    })
    setReady(true)
  }, [repos])

  useEffect(() => {
    void refreshBounds()
  }, [refreshBounds])

  const setSelection = useCallback((next: RangeSelection) => {
    setStored(next)
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next))
    } catch {
      // Persisting is a convenience; the selection still applies to this view.
    }
  }, [])

  const resolved = useMemo(
    () => resolveRange(selection, { clock, bounds }),
    [selection, clock, bounds],
  )

  const value = useMemo(
    () => ({ selection, setSelection, resolved, ready, refreshBounds }),
    [selection, setSelection, resolved, ready, refreshBounds],
  )

  return <RangeContext.Provider value={value}>{children}</RangeContext.Provider>
}

export function useRange(): RangeContextValue {
  const value = useContext(RangeContext)
  if (value === null) {
    throw new Error('useRange must be used inside a RangeProvider')
  }
  return value
}
